import React, { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import {
  BasicDoughnut,
  DoughnutContent,
  StandardContent,
  USDFormatter,
  styled,
} from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'

import CashOwedModal from 'components/CashOwed/CashOwedModal'
import { CustomPortfolioCard } from 'components/CustomPortfolioEntryPoints'
import {
  Portfolio,
  PortfolioTheme,
  Product,
  useTierSubscriptionForPassionsEarlyAccessQuery,
} from 'generated/types'
import { getFormattedDecimalPercentage } from 'pages/invest/SharedFunctions'
import { FormattedDisplayedTheme } from 'pages/portfolio/types'
import {
  PORTFOLIO_BASE_STATS_BONDS,
  PORTFOLIO_BASE_STATS_CRYPTO,
  PORTFOLIO_BASE_STATS_STOCKS,
  PORTFOLIO_BASE_STATS_YTD_EARNINGS,
  PORTFOLIO_BASE_STATS_YTD_RETURN,
  PORTFOLIO_ETF,
  PORTFOLIO_VALUE,
} from 'utils/testing/testIDs'

import CashOwedBar from '../../../../components/CashOwed/CashOwedBar'
import { useCashOwedContext } from '../../../../providers/CashOwed'
import { useCrypto } from '../../../portfolio/Crypto/useCrypto'
import { usePortfolioContent } from '../hooks/usePortfolioContent'
import {
  MinimalRelativeContainer,
  PassionsCustomizeContainer,
  StatTooltipContent,
  StyledStatsBar,
} from '../styled-components'
import { RootProps, StatYTDProps } from '../types'
import { percentFormatter, valueFormatter } from '../utils'
import { EditPortfolioDrawer } from './EditPortfolioDrawer'
import { InvestmentHeader } from './InvestmentHeader'
import Layout from './Layout'
import { LoadingContainer } from './LoadingContainer'

const CashOwedBarContainer = styled.div`
  margin-top: 30px;
`

export type MainContentProps = RootProps & {
  portfolio: Portfolio
  displayData?: (string | undefined)[]
  isProductInvest?: boolean
} & StatYTDProps

export type MainStatsBarProps = {
  totalStocksPercentage: string
  totalBondsPercentage: string
  totalCryptoPercentage: string | null
} & StatYTDProps

const MainStatsBar = ({
  yearToDateReturn,
  yearToDateEarnings,
  totalStocksPercentage,
  totalBondsPercentage,
  totalCryptoPercentage,
}: MainStatsBarProps) => {
  const { getRemoteString } = useRemoteStrings()

  return (
    <StyledStatsBar
      stats={[
        {
          value: percentFormatter(yearToDateReturn),
          description: getRemoteString({ key: 'portfolio.grid.return' }),
          testId: PORTFOLIO_BASE_STATS_YTD_RETURN,
          icon: (
            <AcornsIcon
              icon={AcornsIcon.Icon.UtilityCalendar}
              width={24}
              height={24}
            />
          ),
        },
        {
          value: valueFormatter(yearToDateEarnings),
          description: getRemoteString({ key: 'portfolio.grid.earnings' }),
          testId: PORTFOLIO_BASE_STATS_YTD_EARNINGS,
          icon: (
            <AcornsIcon
              icon={AcornsIcon.Icon.UtilityCoin}
              width={24}
              height={24}
            />
          ),
        },
        {
          value: totalBondsPercentage,
          description: getRemoteString({ key: 'portfolio.grid.bonds' }),
          testId: PORTFOLIO_BASE_STATS_BONDS,
          tooltip: {
            content: (
              <StatTooltipContent>
                {getRemoteString({ key: 'portfolio.stat.base.bonds' })}
              </StatTooltipContent>
            ),
            mobileOverrides: {
              placement: 'top-end',
              offset: -9,
            },
          },
          icon: (
            <AcornsIcon
              icon={AcornsIcon.Icon.UtilityBonds}
              width={24}
              height={24}
            />
          ),
        },
        {
          value: totalStocksPercentage,
          description: getRemoteString({ key: 'portfolio.grid.stocks' }),
          testId: PORTFOLIO_BASE_STATS_STOCKS,
          tooltip: {
            content: (
              <StatTooltipContent>
                {getRemoteString({ key: 'portfolio.stat.base.stocks' })}
              </StatTooltipContent>
            ),
            placement: 'top',
            mobileOverrides: {
              placement: 'top-start',
              offset: 18,
            },
          },
          icon: (
            <AcornsIcon
              icon={AcornsIcon.Icon.UtilityStocks}
              width={24}
              height={24}
            />
          ),
        },
        ...(totalCryptoPercentage
          ? [
              {
                value: totalCryptoPercentage,
                description: getRemoteString({ key: 'portfolio.grid.new_etf' }),
                testId: PORTFOLIO_BASE_STATS_CRYPTO,
                tooltip: {
                  content: (
                    <StatTooltipContent>
                      {getRemoteString({ key: 'portfolio.stat.base.new_etf' })}
                    </StatTooltipContent>
                  ),
                  placement: 'top-end' as any,
                  offset: 9,
                  mobileOverrides: {
                    offset: -9,
                  },
                },
                icon: (
                  <AcornsIcon
                    icon={AcornsIcon.Icon.CryptoDark}
                    width={24}
                    height={24}
                  />
                ),
              },
            ]
          : []),
      ]}
    />
  )
}

export const MainContent: FC<MainContentProps> = ({
  portfolio,
  portfolios,
  onETFPress,
  currentAccountBalance,
  displayData,
  yearToDateEarnings = 0,
  yearToDateReturn = 0,
  isProductInvest,
  graphData,
  selectedView,
}) => {
  const { getRemoteString } = useRemoteStrings()
  const {
    setBeneficiaryId,
    isInvestCashOwed,
    isEarlyCashOwed,
    isLaterCashOwed,
  } = useCashOwedContext()

  const {
    selectedDataIndex,
    totalBondsPercentage,
    totalStocksPercentage,
    totalCryptoPercentage,
    handleSelectedDataChange,
    chartData,
    chartArcColors,
    product,
  } = usePortfolioContent({ graphData, selectedView, portfolio })
  const { beneficiaryId } = useParams<{ beneficiaryId: string }>()
  const isEarly = !!beneficiaryId

  const path = window.location.pathname
  const isLater = path.includes('later')

  const {
    eligibleForCrypto,
    isCryptoDataLoading,
    isPortfolioBeingRebalanced,
    currentPortfolioIncludesCrypto,
  } = useCrypto()

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)

  const { data: tierData } = useTierSubscriptionForPassionsEarlyAccessQuery()
  const products =
    (tierData?.tierSubscriptionByUserId?.tier?.products as Product[]) ?? []
  const baseSecuritiesLength =
    portfolio?.portfolioSecurityAllocations?.length ?? 0

  const portfolioRisk = portfolio?.name
  let formattedTheme = ''
  if (portfolio?.theme === PortfolioTheme.Standard) {
    formattedTheme = FormattedDisplayedTheme.STANDARD
  } else {
    formattedTheme = FormattedDisplayedTheme.SUSTAINABLE
  }

  useEffect(() => {
    if (isEarly) {
      setBeneficiaryId(beneficiaryId)
    }
  }, [beneficiaryId, isEarly, setBeneficiaryId])

  return (
    <LoadingContainer
      portfolios={portfolios}
      isCryptoDataLoading={isCryptoDataLoading}
    >
      {isProductInvest && (
        <InvestmentHeader
          totalEtfCount={baseSecuritiesLength}
          handleEditPress={() => setIsEditDrawerOpen(true)}
        />
      )}
      <MinimalRelativeContainer>
        <BasicDoughnut
          data={chartData}
          colors={chartArcColors}
          onItemHighlight={handleSelectedDataChange}
          options={{
            animation: {
              animateRotate: true,
              animateScale: false,
            },
            round: true,
          }}
        >
          <DoughnutContent
            testId={PORTFOLIO_VALUE}
            defaultContent={
              <StandardContent
                label={getRemoteString({ key: 'portfolio.value' })}
                value={USDFormatter.format(currentAccountBalance || 0)}
              />
            }
            selectedIndex={selectedDataIndex ?? undefined}
            value={getFormattedDecimalPercentage(
              chartData?.[selectedDataIndex!],
            )}
            label={displayData?.[selectedDataIndex!]}
          />
        </BasicDoughnut>
      </MinimalRelativeContainer>
      <CashOwedBarContainer>
        {isEarly
          ? isEarlyCashOwed && <CashOwedBar />
          : isLater
            ? isLaterCashOwed && <CashOwedBar />
            : isInvestCashOwed && <CashOwedBar />}
      </CashOwedBarContainer>
      <Layout
        product={product}
        isPortfolioBeingRebalanced={isPortfolioBeingRebalanced}
        statsBar={
          <>
            <MainStatsBar
              yearToDateEarnings={yearToDateEarnings}
              yearToDateReturn={yearToDateReturn}
              totalBondsPercentage={totalBondsPercentage}
              totalStocksPercentage={totalStocksPercentage}
              totalCryptoPercentage={totalCryptoPercentage}
            />
          </>
        }
        gridProps={{
          testIdPrefix: PORTFOLIO_ETF,
          items: portfolio?.portfolioSecurityAllocations || [],
          onItemPress: onETFPress,
          selectedIndex: selectedDataIndex,
        }}
        eligibleForCrypto={eligibleForCrypto && !!isProductInvest}
      />
      <PassionsCustomizeContainer>
        <CustomPortfolioCard
          investmentProduct={product}
          products={products}
          screen="portfolio"
        />
      </PassionsCustomizeContainer>
      <CashOwedModal />
      <EditPortfolioDrawer
        isDrawerOpen={isEditDrawerOpen}
        setIsDrawerOpen={setIsEditDrawerOpen}
        portfolioRisk={portfolioRisk}
        portfolioTheme={formattedTheme}
        isCryptoInPortfolio={currentPortfolioIncludesCrypto}
        isCustomPortfolio={false}
      />
    </LoadingContainer>
  )
}
